import { Modal } from 'antd';

type AppVersionManifest = {
  version?: unknown;
};

type VersionCheckOptions = {
  currentVersion?: string;
  fetchImpl?: typeof fetch;
  reloadPage?: () => void;
  versionUrl?: string;
};

export const APP_VERSION_CHECK_INTERVAL_MS = 60 * 60 * 1000;

let updateModalVisible = false;

export const resetAppVersionCheckerForTests = () => {
  updateModalVisible = false;
};

const getCurrentVersion = () => process.env.REACT_APP_VERSION || 'unknown';

const getFetch = (fetchImpl?: typeof fetch) => {
  if (fetchImpl) return fetchImpl;
  if (typeof window === 'undefined' || !window.fetch) return undefined;
  return window.fetch.bind(window);
};

const getReloadPage = (reloadPage?: () => void) => {
  if (reloadPage) return reloadPage;
  return () => window.location.reload();
};

export const cleanupLegacyServiceWorkers = () => {
  if (
    typeof navigator === 'undefined' ||
    !('serviceWorker' in navigator) ||
    !navigator.serviceWorker.getRegistrations
  ) {
    return;
  }

  navigator.serviceWorker
    .getRegistrations()
    .then(registrations => {
      registrations.forEach(registration => {
        registration.unregister();
      });
    })
    .catch(() => undefined);
};

export const checkForAppVersionUpdate = async (
  options: VersionCheckOptions = {}
): Promise<boolean> => {
  const fetchVersion = getFetch(options.fetchImpl);
  if (!fetchVersion) return false;

  try {
    const response = await fetchVersion(options.versionUrl || '/version.json', {
      cache: 'no-store',
    });

    if (!response.ok) return false;

    const manifest = (await response.json()) as AppVersionManifest;
    const remoteVersion =
      typeof manifest.version === 'string' ? manifest.version.trim() : '';
    const currentVersion = (options.currentVersion || getCurrentVersion()).trim();

    if (!remoteVersion || remoteVersion === currentVersion || updateModalVisible) {
      return false;
    }

    updateModalVisible = true;
    Modal.info({
      title: 'Nueva versión disponible',
      content:
        'Hay una nueva versión de SmartEco disponible. Actualiza para cargar los últimos cambios.',
      okText: 'Actualizar',
      closable: true,
      maskClosable: true,
      onOk: getReloadPage(options.reloadPage),
      afterClose: () => {
        updateModalVisible = false;
      },
    });

    return true;
  } catch {
    return false;
  }
};

export const startAppVersionChecker = () => {
  if (typeof window === 'undefined') return () => undefined;

  cleanupLegacyServiceWorkers();

  const runCheck = () => {
    void checkForAppVersionUpdate();
  };

  const timeoutId = window.setTimeout(runCheck, 0);
  const intervalId = window.setInterval(runCheck, APP_VERSION_CHECK_INTERVAL_MS);

  window.addEventListener('focus', runCheck);

  return () => {
    window.clearTimeout(timeoutId);
    window.clearInterval(intervalId);
    window.removeEventListener('focus', runCheck);
  };
};
